<template>
    <div class="data-card">
        <div class="data-card-img" @click="selectMedia">
            <img v-if="dataObj.status == 'cdn_upload_completed'" :src="thumbnailUrl" alt="Uploaded Media">
            <div class="data-card-duration">{{ formattedDuration }}</div>
            <div class="data-card-status" :class="statusClass">
                <b-icon :icon="statusIcon" size="is-small"></b-icon>
                <span>{{ statusText }}</span>
            </div>
            <div class="data-card-options">
                <b-dropdown v-if="canDelete" position="is-bottom-left" aria-role="menu">
                    <template #trigger>
                        <button class="button is-small is-white">
                            <b-icon icon="dots-vertical"></b-icon>
                        </button>
                    </template>
                    <b-dropdown-item aria-role="menu-item" @click="$emit('deleteMedia')">
                        <b-icon icon="delete"></b-icon>
                        Delete
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>
        <div class="data-card-info">
            <h1 class="data-card-title" @click="selectMedia">{{ dataObj.file_name }}</h1>
            <div class="data-card-meta" @click="selectMedia">
                <div class="meta-item">
                    <b-icon icon="calendar" size="is-small"></b-icon>
                    <span>{{ formattedDate }}</span>
                </div>
            </div>
            <div class="data-card-content">
                <div class="data-card-progress" v-if="dataObj.status !== 'cdn_upload_completed'">
                    <div class="progress-container">
                        <b-progress :value="parseInt(dataObj.progress)" size="is-medium"
                            :type="progressType"></b-progress>
                        <span class="progress-percentage">{{ dataObj.progress }}%</span>
                    </div>
                </div>
                <div class="data-card-actions" v-else>
                    <b-button size="is-small" type="is-text" icon-left="play" @click="playMedia">
                        Play
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    props: {
        dataObj: {
            type: Object,
            required: true
        },
        canDelete: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        thumbnailUrl() {
            return `${this.$store.state.ASSETS_VIDEO_BASE_URL}watch.rw/${this.dataObj.videoId}/preview.webp`;
        },
        formattedDuration() {
            return this.toHHMMSS(parseFloat(this.dataObj.videoTime));
        },
        formattedDate() {
            return this.moment(this.dataObj.created_at).format('DD/MM/YYYY HH:mm');
        },
        progressType() {
            return this.dataObj.status === 'cdn_upload_completed' ? 'is-success' : 'is-info';
        },
        statusClass() {
            return {
                'status-completed': this.dataObj.status === 'cdn_upload_completed',
                'status-uploading': this.dataObj.status !== 'cdn_upload_completed'
            };
        },
        statusIcon() {
            return this.dataObj.status === 'cdn_upload_completed' ? 'check-circle' : 'cog';
        },
        statusText() {
            return this.dataObj.status === 'cdn_upload_completed' ? 'Ready' : 'Processing';
        }
    },
    methods: {
        selectMedia() {
            let vm = this;
            vm.$emit('click', vm.dataObj);
        },
        toHHMMSS(seconds) {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);

            const parts = [];

            if (hours > 0) {
                parts.push(hours.toString().padStart(2, '0'));
            }

            parts.push(minutes.toString().padStart(2, '0'));
            parts.push(secs.toString().padStart(2, '0'));

            return parts.join(':');
        },
        playMedia() {
            let vm = this;
            vm.$emit('playMedia', vm.dataObj);
        }
    }
}
</script>

<style lang="scss" scoped>
.data-card {
    width: 100%;
    height: 320px;
    /* Fixed height for all cards */
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    background: white;
    display: flex;
    flex-direction: column;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    }

    .data-card-img {
        width: 100%;
        height: 180px;
        position: relative;
        overflow: hidden;
        flex-shrink: 0;
        /* Prevent image from shrinking */

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.5s ease;
        }

        &:hover img {
            transform: scale(1.05);
        }

        .data-card-duration {
            position: absolute;
            bottom: 10px;
            right: 10px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            z-index: 2;
        }

        .data-card-status {
            position: absolute;
            top: 10px;
            left: 10px;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            display: flex;
            align-items: center;
            gap: 4px;
            z-index: 2;

            &.status-completed {
                background: rgba(72, 199, 116, 0.9);
                color: white;
            }

            &.status-uploading {
                background: rgba(50, 152, 220, 0.9);
                color: white;
            }
        }

        .data-card-options {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 2;

            .button {
                background: rgba(255, 255, 255, 0.9);
                border-radius: 50%;
                padding: 0;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

                &:hover {
                    background: white;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                }
            }
        }
    }

    .data-card-info {
        padding: 16px;
        background: white;
        flex-grow: 1;
        /* Allow info section to grow */
        display: flex;
        flex-direction: column;

        .data-card-title {
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 8px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            color: #333;
        }

        .data-card-meta {
            display: flex;
            margin-bottom: 12px;
            font-size: 12px;
            color: #666;

            .meta-item {
                display: flex;
                align-items: center;
                gap: 4px;
            }
        }

        .data-card-content {
            margin-top: auto;
            /* Push content to bottom of card */
        }

        .data-card-progress {
            margin-top: 10px;

            .progress-container {
                position: relative;
                margin-bottom: 10px;

                .progress {
                    height: 12px !important;
                    border-radius: 6px;
                    overflow: hidden;
                }

                .progress-percentage {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 10px;
                    font-weight: 700;
                    color: white;
                    text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
                    z-index: 2;
                }
            }

            .progress-details {
                display: flex;
                justify-content: space-between;
                margin-top: 6px;

                .progress-text {
                    font-size: 14px;
                    font-weight: 600;
                    color: #3298dc;
                }
            }
        }

        .data-card-actions {
            display: flex;
            justify-content: center;
            margin-top: 10px;

            .button {
                font-size: 14px;
                padding: 0 12px;
                height: auto;
                font-weight: 500;

                &:hover {
                    background: transparent;
                    color: var(--wecodefy-secondary-color, #9fc953);
                }
            }
        }
    }
}
</style>